<template>
	<SuiSelectListHeaderWithSearch
		:search-query="searchQuery"
		:search-placeholder="$t('user_assign_select_find_user_or_role')"
		:variant="SuiSelectListSearchVariant.INLINE"
		:has-auto-focus="hasAutoFocus"
		class="user-assign-select-inline-header"
		@close="emit('close')"
		@search="emit('search', $event)"
	>
		<template #title>
			<span class="user-assign-select-inline-header__title">
				{{ activeTitle }}
			</span>
		</template>

		<template v-if="activeRoleFilterId" #close>
			{{ t('user_assign_select_inline_back') }}
		</template>
		<template v-else-if="isCloseButtonEnabled" #close>
			{{ t('close') }}
		</template>

		<template #content>
			<Transition name="user-assign-select-inline-header__expand">
				<div
					v-if="totalItemsCount > 0"
					class="user-assign-select-inline-header__content"
				>
					<div class="user-assign-select-inline-header__wrapper">
						<div class="user-assign-select-inline-header__inner">
							<div
								ref="$usersList"
								class="user-assign-select-inline-header__list"
								:class="listClassAddons"
							>
								<template
									v-for="item in rolesSorted.slice(
										0,
										visibleRoleSlotCount
									)"
									:key="item.id"
								>
									<UserAssignSelectHeaderRoleItem
										v-if="item.type === RoleSlotType.Role"
										:role="item.role"
										:allocated-time="
											allocatedTimeInputs.roles[
												item.role.id
											] ?? ''
										"
										:has-individual-time-allocation="
											hasIndividualTimeAllocation
										"
										:has-custom-allocation="
											hasCustomAllocation
										"
										:confirmed-allocated-times="
											confirmedAllocatedTimes
										"
										:active-role-filter-id="
											activeRoleFilterId
										"
										@update:allocated-time="
											allocatedTimeInputs.roles[
												item.role.id
											] = $event
										"
										@focus="
											currentlyFocusedInput = item.role.id
										"
										@blur="
											currentlyFocusedInput = undefined
										"
										@update:active-role-filter-id="
											emit(
												'update:active-role-filter-id',
												item.role.id
											)
										"
									/>
									<UserAssignSelectHeaderUserItem
										v-else
										:user="item.user"
										:availability="
											getUserAvailability(item.user.id)
										"
										:extra-used-availability="
											getExtraUsedAvailability(
												item.user.id
											)
										"
										:allocated-time="
											allocatedTimeInputs.users[
												item.user.id
											] ?? ''
										"
										:has-individual-time-allocation="
											hasIndividualTimeAllocation
										"
										:has-custom-allocation="
											hasCustomAllocation
										"
										:confirmed-allocated-times="
											confirmedAllocatedTimes
										"
										:active-role-filter-id="
											activeRoleFilterId
										"
										@update:allocated-time="
											allocatedTimeInputs.users[
												item.user.id
											] = $event
										"
										@focus="
											currentlyFocusedInput = item.user.id
										"
										@blur="
											currentlyFocusedInput = undefined
										"
										@unselect="
											emit('unselect', item.user.id)
										"
									/>
								</template>
								<UserAssignSelectHeaderUserItem
									v-for="user in usersSorted.slice(
										0,
										visibleUsersCount
									)"
									:key="user.id"
									:user="user"
									:availability="getUserAvailability(user.id)"
									:extra-used-availability="
										getExtraUsedAvailability(user.id)
									"
									:allocated-time="
										allocatedTimeInputs.users[user.id] ?? ''
									"
									:has-individual-time-allocation="
										hasIndividualTimeAllocation
									"
									:has-custom-allocation="hasCustomAllocation"
									:confirmed-allocated-times="
										confirmedAllocatedTimes
									"
									:active-role-filter-id="activeRoleFilterId"
									@update:allocated-time="
										allocatedTimeInputs.users[user.id] =
											$event
									"
									@focus="currentlyFocusedInput = user.id"
									@blur="currentlyFocusedInput = undefined"
									@unselect="emit('unselect', user.id)"
								/>
								<UserAssignSelectHeaderExpandedList
									:roles-sorted="rolesSorted"
									:users-sorted="usersSorted"
									:allocated-time-inputs="allocatedTimeInputs"
									:has-individual-time-allocation="
										hasIndividualTimeAllocation
									"
									:has-custom-allocation="hasCustomAllocation"
									:confirmed-allocated-times="
										confirmedAllocatedTimes
									"
									:active-role-filter-id="activeRoleFilterId"
									:task-users-availability="
										taskUsersAvailability
									"
									:extra-count="extraCount"
									:is-all-assignees-shown="
										isAllAssigneesShown
									"
									:dropdown-reference="dropdownReference"
									:users-list-element="$usersList"
									:show-more-label="$t('show_more')"
									:expanded-list-aria-label="
										$t('user_assign_expand_user_list')
									"
									:collapse-label="$t('collapse')"
									@update:role-allocated-time="
										(roleId, value) =>
											(allocatedTimeInputs.roles[roleId] =
												value)
									"
									@update:user-allocated-time="
										(userId, value) =>
											(allocatedTimeInputs.users[userId] =
												value)
									"
									@focus="currentlyFocusedInput = $event"
									@blur="currentlyFocusedInput = undefined"
									@update:active-role-filter-id="
										emit(
											'update:active-role-filter-id',
											$event
										)
									"
									@unselect="emit('unselect', $event)"
								/>
							</div>

							<div
								v-if="hasIndividualTimeAllocation"
								class="user-assign-select-inline-header__toggle"
								:class="toggleClassAddons"
							>
								<SuiToggle
									:model-value="hasCustomAllocation"
									:disabled="!!activeRoleFilterId"
									:label="
										$t('avatars__individual_allocation')
									"
									@update:model-value="
										emit(
											'update:has-custom-allocation',
											$event
										)
									"
								/>
							</div>
						</div>
					</div>
				</div>
			</Transition>
		</template>
	</SuiSelectListHeaderWithSearch>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import {
	SuiToggle,
	SuiSelectListHeaderWithSearch,
	SuiSelectListSearchVariant,
} from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import {
	type User,
	type UserAssignSelectInlineAvailabilityQuery,
	type UserAssignSelectUserFragment,
} from '@/generated/graphql';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { shortDurationValue, getDurationInSeconds } from '@/utils/formatting';
import {
	UserAssignSelectHeaderUserItem,
	UserAssignSelectHeaderRoleItem,
	UserAssignSelectHeaderExpandedList,
} from '../UserAssignSelect/Header';
import { useCurrentUser } from '@/features/Auth';
import { sortSelectedRoles, sortSelectedUsers } from '../../utils';
import {
	RoleSlotType,
	type PinnedUserPositions,
	type UserAssignSelectRole,
} from '../../types';
import { MAX_AVATARS_SHOWN_WITHOUT_HIDING } from '../../constants';

export interface UserAssignSelectInlineHeaderProps {
	title?: string;
	searchQuery: string;
	usersList: UserAssignSelectUserFragment[];
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	pinnedUserPositions?: PinnedUserPositions;
	hasIndividualTimeAllocation?: boolean;
	hasCustomAllocation?: boolean;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	taskUsersAvailability?: UserAssignSelectInlineAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
	isCloseButtonEnabled?: boolean;
	hasAutoFocus?: boolean;
}

const {
	title = '',
	searchQuery,
	usersList,
	selectedUsers,
	selectedRoles,
	pinnedUserPositions = new Map(),
	hasIndividualTimeAllocation = false,
	hasCustomAllocation = false,
	confirmedAllocatedTimes,
	allocatedTimes,
	taskUsersAvailability = [],
	activeRoleFilterId,
	isCloseButtonEnabled = false,
	hasAutoFocus = false,
} = defineProps<UserAssignSelectInlineHeaderProps>();

const emit = defineEmits<{
	close: [];
	search: [value: string];
	unselect: [userId: User['id']];
	'update:has-custom-allocation': [value: boolean];
	'update:allocated-times': [payload: TaskAssigneeInputDuration];
	'update:active-role-filter-id': [roleId: UserAssignSelectRole['id']];
}>();

const { t } = useTranslation();
const { currentUser } = useCurrentUser();

const $usersList = ref<HTMLDivElement | null>(null);
const allocatedTimeInputs = reactive<TaskAssigneeInputDuration>({
	users: {},
	roles: {},
});
const currentlyFocusedInput = ref<string>();

const users = computed(() => {
	return usersList.filter((user) => selectedUsers.includes(user.id));
});

const activeTitle = computed(() => {
	if (activeRoleFilterId) {
		const role = selectedRoles.find(
			(role) => role.id === activeRoleFilterId
		);
		return t('user_assign_select_assign_to', { role: role?.name ?? '' });
	}

	return title;
});

const rolesSorted = computed(() =>
	sortSelectedRoles({
		users: users.value,
		selectedRoles,
		pinnedUserPositions,
	})
);

const usersSorted = computed(() =>
	sortSelectedUsers({
		users: users.value,
		pinnedUserPositions,
		currentUserId: currentUser.value?.id,
	})
);

const totalItemsCount = computed(() => {
	return rolesSorted.value.length + usersSorted.value.length;
});

const listClassAddons = computed(() => ({
	'user-assign-select-inline-header__list--with-spacing':
		totalItemsCount.value > 0 && hasIndividualTimeAllocation,
}));

const toggleClassAddons = computed(() => ({
	'user-assign-select-inline-header__toggle--disabled': !!activeRoleFilterId,
}));

const visibleRoleSlotCount = computed(() => {
	return Math.min(rolesSorted.value.length, MAX_AVATARS_SHOWN_WITHOUT_HIDING);
});

const visibleUsersCount = computed(() => {
	return Math.max(
		0,
		MAX_AVATARS_SHOWN_WITHOUT_HIDING - visibleRoleSlotCount.value
	);
});

const extraCount = computed(() => {
	const shouldShowExtraCount =
		totalItemsCount.value > MAX_AVATARS_SHOWN_WITHOUT_HIDING;

	if (!shouldShowExtraCount) {
		return 0;
	}

	const totalVisible = visibleRoleSlotCount.value + visibleUsersCount.value;

	return totalItemsCount.value - totalVisible;
});

const isAllAssigneesShown = computed(() => {
	return extraCount.value <= 0;
});

const dropdownReference = () =>
	document.querySelector('.user-assign-select-inline-header__list');

const getUserAvailability = (userId: UserAssignSelectUserFragment['id']) => {
	return taskUsersAvailability?.find(
		(availability) => availability.user.id === userId
	);
};

const getExtraUsedAvailability = (
	userId: UserAssignSelectUserFragment['id']
) => {
	const allocatedTimeInSeconds = getDurationInSeconds(
		allocatedTimeInputs.users[userId] ?? '0'
	);
	const confirmedTime = confirmedAllocatedTimes?.users.get(userId) ?? 0;

	return allocatedTimeInSeconds - confirmedTime;
};

watch(
	() => allocatedTimes,
	(newAllocatedTimes) => {
		if (!newAllocatedTimes) {
			return;
		}

		for (const userId of Object.keys(allocatedTimeInputs.users)) {
			if (!selectedUsers.includes(userId)) {
				delete allocatedTimeInputs.users[userId];
			}
		}

		for (const roleId of Object.keys(allocatedTimeInputs.roles)) {
			if (!selectedRoles.some((role) => role.id === roleId)) {
				delete allocatedTimeInputs.roles[roleId];
			}
		}

		for (const user of selectedUsers) {
			const time = newAllocatedTimes.users.get(user);
			if (time !== undefined && currentlyFocusedInput.value !== user) {
				allocatedTimeInputs.users[user] = shortDurationValue(time);
			}
		}

		for (const role of selectedRoles) {
			const time = newAllocatedTimes.roles.get(role.id);
			if (time !== undefined && currentlyFocusedInput.value !== role.id) {
				allocatedTimeInputs.roles[role.id] = shortDurationValue(time);
			}
		}
	},
	{ immediate: true, deep: true }
);

watch(
	allocatedTimeInputs,
	(newAllocatedTimeInputs) =>
		emit('update:allocated-times', newAllocatedTimeInputs),
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.user-assign-select-inline-header {
	&__content {
		display: grid;
		grid-template-rows: 1fr;
		margin-top: 0.5rem;
	}

	&__wrapper {
		min-height: 0;
		overflow: visible;
	}

	&__inner {
		padding: 1rem 0;
	}

	&__list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;

		&--with-spacing {
			margin-bottom: 2rem;
		}
	}

	&__toggle {
		display: inline-block;
		transition: opacity 200ms ease;

		&--disabled {
			opacity: 0.5;
			pointer-events: none;
		}
	}

	&__expand {
		&-enter-active,
		&-leave-active {
			transition:
				grid-template-rows 0.3s linear,
				opacity 0.3s linear;
			overflow: hidden;
		}

		&-enter-from,
		&-leave-to {
			grid-template-rows: 0fr;
			opacity: 0;
		}
	}
}
</style>
